/**
 * Business Profile Analyzer
 * Builds a structured BusinessProfile from the user's dream text using OpenAI.
 * Called once per project — result is stored globally and consumed by all modules.
 */

import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { getAILanguageInstruction } from '@/lib/i18n';
import type { BusinessProfile } from './types';
import {
  BUSINESS_PROFILE_EXPLICIT_CALLER,
  BUSINESS_PROFILE_FEATURE,
  BUSINESS_PROFILE_OPERATION_KEY,
  buildBusinessProfileIdempotencyKey,
} from './policy';

const ANALYSIS_SYSTEM_PROMPT = `You are an expert Creative Director and Business Strategist at a world-class branding agency.
Your job is to analyze a business dream and produce a comprehensive, structured Business Profile JSON object.
You must respond with ONLY valid JSON — no markdown, no explanation, no code fences.
Be specific, creative, and commercially realistic. Every field must be filled with meaningful, actionable content.`;

function buildAnalysisPrompt(dream: string, langInstruction: string): string {
  return `${langInstruction}Analyze this business dream and return a complete Business Profile JSON:

Dream: "${dream}"

Return ONLY this JSON structure (all string values must be in the language specified by the language instruction above):

{
  "business_type": "string — e.g. e-commerce, service business, SaaS, content creator, restaurant, etc.",
  "industry": "string — specific industry sector",
  "niche": "string — precise niche within the industry",
  "audience": {
    "primary": "string — main target demographic with age, lifestyle, values",
    "secondary": "string — secondary audience segment",
    "pain_points": ["string", "string", "string"],
    "desires": ["string", "string", "string"]
  },
  "tone": "string — 3-4 adjectives describing brand voice",
  "brand_personality": ["adjective1", "adjective2", "adjective3", "adjective4"],
  "brand_archetype": "string — one of: The Creator, The Caregiver, The Explorer, The Sage, The Innocent, The Hero, The Outlaw, The Magician, The Ruler, The Lover, The Jester, The Everyman",
  "visual_style": "string — overall visual aesthetic description",
  "color_direction": {
    "primary": "string — primary color description with mood",
    "secondary": "string — secondary color description",
    "accent": "string — accent color description",
    "mood": "string — overall color mood"
  },
  "typography_direction": {
    "headline": "string — headline font style description",
    "body": "string — body font style description",
    "accent": "string — accent/decorative font description"
  },
  "image_style": "string — photography and visual content style",
  "pricing_level": "budget OR mid-range OR premium OR luxury",
  "monetization_methods": ["method1", "method2", "method3"],
  "recommended_products_services": [
    {"name": "string", "description": "string", "price_range": "string"},
    {"name": "string", "description": "string", "price_range": "string"},
    {"name": "string", "description": "string", "price_range": "string"},
    {"name": "string", "description": "string", "price_range": "string"},
    {"name": "string", "description": "string", "price_range": "string"}
  ],
  "marketing_channels": ["channel1", "channel2", "channel3", "channel4"],
  "launch_strategy": {
    "phase1": "string — Foundation phase summary (days 1-30)",
    "phase2": "string — Build phase summary (days 31-60)",
    "phase3": "string — Launch phase summary (days 61-90)",
    "quick_wins": ["action1", "action2", "action3"]
  },
  "content_strategy": {
    "pillars": ["pillar1", "pillar2", "pillar3"],
    "formats": ["format1", "format2", "format3"],
    "posting_frequency": "string",
    "hooks": ["hook1", "hook2", "hook3", "hook4"]
  },
  "keywords": ["keyword1", "keyword2", "keyword3", "keyword4", "keyword5", "keyword6", "keyword7", "keyword8"],
  "business_goals": {
    "short_term": ["goal1", "goal2", "goal3"],
    "long_term": ["goal1", "goal2", "goal3"]
  }
}`;
}

export type AnalyzeBusinessDreamOptions = {
  projectId?: string;
  attemptId?: string;
  signal?: AbortSignal;
};

export async function analyzeBusinessDream(
  dream: string,
  language: string = 'en',
  options: AnalyzeBusinessDreamOptions = {}
): Promise<BusinessProfile> {
  const langInstruction = getAILanguageInstruction(language as any);
  const prompt = buildAnalysisPrompt(dream, langInstruction);

  const attemptId =
    options.attemptId?.trim() ||
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `attempt-${Date.now()}`);
  const projectId = options.projectId?.trim() || 'pending';
  const idempotencyKey = buildBusinessProfileIdempotencyKey({ projectId, attemptId });

  const result = await getChatCompletion(
    'OPEN_AI',
    'gpt-4o',
    [
      { role: 'system', content: ANALYSIS_SYSTEM_PROMPT },
      { role: 'user', content: prompt },
    ],
    {
      max_completion_tokens: 2000,
      response_format: { type: 'json_object' },
    },
    {
      feature: BUSINESS_PROFILE_FEATURE,
      operationKey: BUSINESS_PROFILE_OPERATION_KEY,
      caller: BUSINESS_PROFILE_EXPLICIT_CALLER,
      expectJson: true,
      idempotencyKey,
      signal: options.signal,
    }
  );

  const content = result?.choices?.[0]?.message?.content ?? '{}';

  let parsed: any;
  try {
    parsed = JSON.parse(content);
  } catch {
    // Fallback: try to extract JSON from the response
    const match = content.match(/\{[\s\S]*\}/);
    if (match) {
      parsed = JSON.parse(match[0]);
    } else {
      throw new Error('Failed to parse Business Profile from AI response');
    }
  }

  return {
    ...parsed,
    generated_at: new Date().toISOString(),
    dream_input: dream,
    language,
  } as BusinessProfile;
}

/**
 * Build section-specific prompts enriched with the Business Profile context.
 * This replaces the generic dream-only prompts used previously.
 */
export function buildEnrichedPrompt(
  sectionId: string,
  dream: string,
  profile: BusinessProfile,
  langInstruction: string
): string {
  const ctx = buildProfileContext(profile);

  const prompts: Record<string, string> = {
    vision: `${langInstruction}You are a business vision strategist. 

Business Profile:
${ctx}

Original Dream: "${dream}"

Create a structured Vision document with:
1. **Dream Summary** – A clear 2-3 sentence articulation of the dream
2. **Core Goals** – 3-5 specific, measurable goals aligned with the ${profile.business_type} model
3. **Why This Matters** – The deeper motivation and impact for the ${profile.audience.primary}
4. **Success Vision** – What success looks like in 12 months for this ${profile.industry} business
5. **Key Challenges** – 2-3 potential obstacles specific to the ${profile.niche} space

Be specific, inspiring, and actionable. Format with clear headers.`,

    brand: `${langInstruction}You are a brand strategist and creative director.

Business Profile:
${ctx}

Original Dream: "${dream}"

Generate a complete Brand Kit perfectly aligned with the ${profile.brand_archetype} archetype and ${profile.visual_style} aesthetic:
1. **Brand Name Ideas** – 5 creative, memorable names for this ${profile.niche} business
2. **Brand Personality** – Voice and tone: ${profile.tone}
3. **Color Palette** – Based on the ${profile.color_direction.mood} direction: ${profile.color_direction.primary}, ${profile.color_direction.secondary}, ${profile.color_direction.accent}
4. **Slogans & Taglines** – 5 options that resonate with ${profile.audience.primary}
5. **Logo Concept** – 2 concepts matching the ${profile.visual_style} style
6. **Typography** – ${profile.typography_direction.headline} headlines, ${profile.typography_direction.body} body

Make it feel ${profile.brand_personality.join(', ')}.`,

    products: `${langInstruction}You are a product strategist.

Business Profile:
${ctx}

Original Dream: "${dream}"

Create a Product & Services Blueprint for this ${profile.pricing_level} ${profile.niche} business:
1. **Core Offering** – Primary product/service with description
2. **Product Line** – Based on these recommendations: ${profile.recommended_products_services.map(p => p.name).join(', ')}
3. **Pricing Strategy** – ${profile.pricing_level} tier pricing with specific price points
4. **Revenue Streams** – ${profile.monetization_methods.join(', ')}
5. **Unique Value Proposition** – What sets this apart in the ${profile.industry} space
6. **Quick Win** – One product/service to launch in 30 days

Be specific with pricing ranges for the ${profile.audience.primary} audience.`,

    content: `${langInstruction}You are a social media strategist and content creator.

Business Profile:
${ctx}

Original Dream: "${dream}"

Create a Content Strategy Pack for ${profile.marketing_channels.join(', ')}:
1. **Content Pillars** – ${profile.content_strategy.pillars.join(', ')}
2. **Post Ideas** – 5 specific concepts using these hooks: ${profile.content_strategy.hooks.slice(0, 2).join(', ')}
3. **Caption Templates** – 3 ready-to-use frameworks in the ${profile.tone} voice
4. **Video Ideas** – 4 concepts in ${profile.content_strategy.formats.join('/')} format
5. **Hashtag Strategy** – 10 hashtags using keywords: ${profile.keywords.slice(0, 4).join(', ')}
6. **First 7 Posts** – Day-by-day calendar posting ${profile.content_strategy.posting_frequency}

Make content feel authentic to the ${profile.brand_personality.join(', ')} brand personality.`,

    launchPlan: `${langInstruction}You are a business launch strategist.

Business Profile:
${ctx}

Original Dream: "${dream}"

Create a 90-Day Launch Roadmap for this ${profile.business_type}:

**Phase 1: Foundation (Days 1-30)** — ${profile.launch_strategy.phase1}
- 5 specific action items

**Phase 2: Build (Days 31-60)** — ${profile.launch_strategy.phase2}
- 5 specific action items

**Phase 3: Launch (Days 61-90)** — ${profile.launch_strategy.phase3}
- 5 specific action items

**Key Milestones:**
- Week 1, Month 1, Month 3 milestones

**Immediate Next Actions (Do This Today):**
${profile.launch_strategy.quick_wins.map(w => `- ${w}`).join('\n')}

Focus on ${profile.marketing_channels.join(', ')} channels and ${profile.monetization_methods.join(', ')} revenue streams.`,
  };

  return prompts[sectionId] ?? `${langInstruction}You are an expert business strategist. Dream: "${dream}"\n\nBusiness context: ${ctx}\n\nProvide detailed, actionable guidance for the ${sectionId} section.`;
}

/**
 * Build visual prompts enriched with Business Profile context.
 */
export function buildEnrichedVisualPrompts(
  sectionId: string,
  dream: string,
  profile: BusinessProfile
): { label: string; prompt: string }[] {
  const style = profile.visual_style;
  const colors = `${profile.color_direction.primary}, ${profile.color_direction.secondary}, ${profile.color_direction.accent}`;
  const mood = profile.color_direction.mood;
  const niche = profile.niche;
  const pricing = profile.pricing_level;
  const personality = profile.brand_personality?.slice(0, 2).join(', ') || 'professional';

  const promptSets: Record<string, { label: string; prompt: string }[]> = {
    brand: [
      {
        label: 'Logo Brand Mockup',
        prompt: `Premium brand identity presentation for a ${niche} business. The logo displayed on a realistic storefront sign with ${colors} color palette. ${style} aesthetic, ${mood} atmosphere. Commercial photography quality, elegant lighting, architectural context, ultra-realistic, high-end brand presentation, no clipart, no flat icons.`,
      },
      {
        label: 'Business Card Mockup',
        prompt: `Luxury business card mockup for a ${niche} brand. ${pricing} market positioning. Cards displayed on a marble or premium surface with soft studio lighting. ${colors} color scheme, ${style} design, ${personality} personality. Realistic commercial photography, premium stationery, elegant composition, professional brand identity.`,
      },
      {
        label: 'Color Palette Board',
        prompt: `Professional brand color palette presentation board for a ${niche} business. ${colors} color swatches displayed as elegant paint chips on a clean white surface with soft shadows. ${mood} mood, ${style} aesthetic. Commercial design quality, labeled palette, premium brand identity board, realistic studio photography.`,
      },
      {
        label: 'Brand Identity Mockup',
        prompt: `Complete brand identity mockup for a ${niche} business. Branded packaging, business cards, and stationery arranged in a flat lay composition. ${colors} color palette, ${style} aesthetic, ${mood} feel. ${pricing} market positioning, commercial photography quality, premium brand presentation, realistic materials and textures.`,
      },
      {
        label: 'Signage Mockup',
        prompt: `Realistic wall signage and brand presentation for a ${niche} business. Logo displayed on a premium interior wall sign with professional lighting. ${colors} palette, ${style} design language, ${mood} atmosphere. High-end commercial photography, architectural interior, ${pricing} brand positioning, ultra-realistic rendering.`,
      },
      {
        label: 'Brand Style Board',
        prompt: `Premium brand style mood board for a ${niche} business. Curated collage of typography samples, color swatches, texture swatches, and lifestyle imagery. ${colors} palette, ${style} aesthetic, ${mood} atmosphere. ${personality} brand personality. Commercial design quality, editorial layout, professional brand identity presentation.`,
      },
    ],
    products: [
      {
        label: 'Product Mockup',
        prompt: `Professional product mockup for ${niche}. ${style} aesthetic, ${colors} palette. Clean studio photography on white or neutral background, ${pricing} market presentation. Realistic product photography, soft shadows, commercial quality, no clipart, premium product display.`,
      },
      {
        label: 'Packaging Concept',
        prompt: `Premium packaging design mockup for ${niche}. ${style} style, ${colors} colors. Realistic 3D packaging on a clean surface with soft studio lighting. ${mood} feel, ${pricing} market positioning. Commercial photography quality, elegant unboxing presentation, premium brand packaging.`,
      },
      {
        label: 'Product Photography',
        prompt: `Lifestyle product photography for ${niche}. ${style} aesthetic, ${colors} tones. Natural light, styled scene with props and context, ${mood} atmosphere. ${pricing} brand feel, commercial photography quality, aspirational lifestyle imagery, realistic and polished.`,
      },
      {
        label: 'Storefront Display',
        prompt: `Realistic storefront or retail display for ${niche}. ${style} design, ${colors} palette. Clean, inviting retail environment with professional lighting. ${pricing} market positioning, commercial photography quality, premium retail presentation, architectural context.`,
      },
      {
        label: 'Service Presentation',
        prompt: `Professional service presentation card or brochure mockup for ${niche}. ${style} layout, ${colors} colors. Printed on premium paper with soft shadows, ${mood} feel. Commercial design quality, clear service offering, ${pricing} market positioning, realistic stationery mockup.`,
      },
      {
        label: 'Product Catalog',
        prompt: `Premium product catalog or menu design mockup for ${niche}. ${style} design, ${colors} palette. Realistic printed catalog on a clean surface with soft lighting. Modern editorial layout, ${pricing} positioning, commercial photography quality, professional product presentation.`,
      },
    ],
    content: [
      {
        label: 'Instagram Post Mockup',
        prompt: `Professional Instagram post mockup for ${niche} brand. ${style} aesthetic, ${colors} palette. Displayed on a realistic smartphone screen with soft shadows. Square format, ${mood} feel, ${personality} brand voice. Commercial social media design quality, polished and on-brand, realistic device mockup.`,
      },
      {
        label: 'Reel Cover Mockup',
        prompt: `Short-form video thumbnail mockup for ${niche}. ${style} style, ${colors} colors. Displayed on a realistic phone screen, vertical format. Eye-catching, ${mood} mood, commercial social media quality. Polished design, realistic device mockup, professional content creator aesthetic.`,
      },
      {
        label: 'Pinterest Pin Mockup',
        prompt: `Pinterest pin design mockup for ${niche}. ${style} aesthetic, ${colors} palette. Tall vertical format displayed on a realistic tablet or phone screen. ${mood} atmosphere, inspirational imagery, commercial design quality, professional content marketing aesthetic.`,
      },
      {
        label: 'Facebook Ad Creative',
        prompt: `Facebook advertisement creative mockup for ${niche}. ${style} design, ${colors} colors. Displayed in a realistic Facebook feed on a laptop or desktop screen. Compelling visual, ${personality} tone, conversion-focused design. Commercial advertising quality, professional digital marketing aesthetic.`,
      },
      {
        label: 'Story Template Mockup',
        prompt: `Instagram story template mockup for ${niche}. ${style} aesthetic, ${colors} palette. Vertical format displayed on a realistic smartphone screen. ${mood} feel, ${personality} brand voice. Commercial social media design quality, polished template, realistic device mockup.`,
      },
      {
        label: 'Ad Creative Concept',
        prompt: `Social media ad creative for ${niche}. ${style} style, ${colors} colors. Strong visual hook, ${personality} tone, ${pricing} market. Commercial advertising quality, professional digital marketing design, polished and conversion-focused, realistic mockup presentation.`,
      },
    ],
    launchPlan: [
      {
        label: 'Launch Flyer Mockup',
        prompt: `Launch announcement flyer mockup for ${niche}. ${style} design, ${colors} palette. Realistic printed flyer displayed on a clean surface with soft shadows. Bold, exciting, ${mood} feel, professional event poster quality. Commercial print design, premium paper texture, realistic mockup.`,
      },
      {
        label: 'Landing Page Hero',
        prompt: `Website landing page hero section mockup for ${niche}. ${style} aesthetic, ${colors} colors. Displayed on a realistic laptop or desktop screen. Clean, conversion-focused, ${pricing} web design quality. Commercial web design, professional UI/UX, realistic device mockup, premium digital product presentation.`,
      },
      {
        label: 'Lead Magnet Cover',
        prompt: `Lead magnet or free guide cover design mockup for ${niche}. ${style} design, ${colors} palette. Realistic ebook or PDF cover displayed on a clean surface. ${mood} feel, professional publication quality. Commercial design, premium digital product presentation, realistic 3D book or document mockup.`,
      },
      {
        label: 'Campaign Visual',
        prompt: `Marketing campaign visual for ${niche}. ${style} aesthetic, ${colors} palette. Multi-channel creative displayed as a cohesive set of mockups — phone, laptop, and print. ${personality} tone, commercial advertising quality, professional campaign presentation, realistic device and print mockups.`,
      },
      {
        label: 'Promotional Banner',
        prompt: `Promotional banner design mockup for ${niche}. ${style} design, ${colors} colors. Displayed as a realistic digital banner on a website or as a printed banner stand. Eye-catching, ${mood} feel, ${pricing} market. Commercial advertising quality, professional marketing design, realistic mockup.`,
      },
      {
        label: 'Funnel Mockup',
        prompt: `Marketing funnel visual for ${niche}. ${style} infographic design, ${colors} colors. Clean, professional diagram showing customer journey from awareness to purchase. Commercial design quality, modern business infographic, polished and presentation-ready, ${pricing} brand positioning.`,
      },
    ],
  };

  return promptSets[sectionId] ?? [];
}

function buildProfileContext(profile: BusinessProfile): string {
  return `- Business Type: ${profile.business_type}
- Industry: ${profile.industry}
- Niche: ${profile.niche}
- Target Audience: ${profile.audience.primary}
- Brand Tone: ${profile.tone}
- Brand Personality: ${profile.brand_personality.join(', ')}
- Visual Style: ${profile.visual_style}
- Color Direction: ${profile.color_direction.mood} (${profile.color_direction.primary}, ${profile.color_direction.secondary}, ${profile.color_direction.accent})
- Pricing Level: ${profile.pricing_level}
- Marketing Channels: ${profile.marketing_channels.join(', ')}
- Keywords: ${profile.keywords.join(', ')}`;
}
